/**
 * Einheitliches Deep-Link-Schema (BEN-92) — EINE Quelle der Wahrheit fuer alle
 * Sprung-Ziele quer durch das Cockpit:
 *   (a) OpenProject-Arbeitspakete (op_url)
 *   (b) OpenSearch-Dashboards (Cockpit / OE-Board / Steuerung & Wirkung / Systemgesundheit)
 *   (c) interne Cross-Links (KPI -> Treiberbaum -> Prozessschritt -> Evidence)
 *
 * Regeln: externe Ziele oeffnen in neuem Tab; fehlt eine Ziel-ID, geben die
 * Builder null zurueck -> die Link-Komponente blendet sie sauber aus (kein
 * toter Link).
 */

export type DeepLinkKind = "openproject" | "dashboard" | "cockpit" | "extern" | "intern";

export const OPENPROJECT_BASE = "https://project.impulse.kim";
export const OPENSEARCH_DASH_BASE =
  "https://opensearch.ben-e-fit.ai/app/dashboards#/view";

export type DashboardKey =
  | "cockpit"
  | "oe-board"
  | "steuerung-wirkung"
  | "systemgesundheit";

export const DASHBOARDS: Record<DashboardKey, { id: string; label: string; desc: string }> = {
  cockpit: { id: "kid021-1und1-cockpit", label: "KID-021 Cockpit", desc: "Programm- & Gold-KPI-Dashboard" },
  "oe-board": { id: "kid021-1und1-oe-board", label: "OE-Board", desc: "Ressort × Phase Gate-Matrix" },
  "steuerung-wirkung": { id: "kid021-1und1-steuerung-wirkung", label: "Steuerung & Wirkung", desc: "Methodik, Runs, Wirkungs-Map" },
  systemgesundheit: { id: "kid021-1und1-systemgesundheit", label: "Systemgesundheit", desc: "Betriebs-/Health-Dashboard" },
};

/** Konkrete View-URL eines OpenSearch-Dashboards. */
export function dashboardUrl(key: DashboardKey): string {
  return `${OPENSEARCH_DASH_BASE}/${DASHBOARDS[key].id}`;
}

/** OpenProject-Arbeitspaket. Ohne gueltige numerische ID -> null (kein toter Link). */
export function openProjectWpUrl(wpId: string | number | null | undefined): string | null {
  if (wpId == null) return null;
  const id = String(wpId).trim();
  if (!/^[0-9]+$/.test(id)) return null;
  return `${OPENPROJECT_BASE}/work_packages/${id}`;
}

/** OpenProject-Programmuebersicht (Arbeitspakete der 1&1-Projekte). */
export function openProjectProgramUrl(): string {
  return `${OPENPROJECT_BASE}/projects`;
}

/**
 * Welches Dashboard passt am besten zu einer KPI? Default: Cockpit (Gold-KPIs);
 * OpenProject-Kennzahlen verweisen aufs Steuerungs-Dashboard.
 */
export function kpiDashboardKey(metricKey?: string | null): DashboardKey {
  const k = (metricKey || "").toLowerCase();
  if (k.startsWith("openproject_")) return "steuerung-wirkung";
  return "cockpit";
}

/** Interner Cross-Link: KPI -> Treiberbaum. */
export function driverTreeRoute(kpiId?: string | null): string {
  return kpiId ? `/app/trees/${encodeURIComponent(kpiId)}` : "/app/trees";
}

/** Interner Cross-Link: Beleg/Evidence (Agent Runs). */
export function evidenceRoute(runId?: string | null): string {
  return runId ? `/app/agents?run=${encodeURIComponent(runId)}` : "/app/agents";
}

export type CitationLike = {
  source?: string | null;
  path?: string | null;
  href?: string | null;
  label?: string | null;
};

/**
 * Loest fuer einen Wissensbasis-Beleg (Citation) das beste externe Ziel auf:
 * OpenProject-WP (wenn Quelle/Pfad/Label eine WP-Nummer enthaelt), sonst — bei
 * OpenProject-Quellen — die Programmuebersicht. Nicht aufloesbar -> null
 * (dann wird die Citation ohne Link als reiner Beleg-Chip angezeigt).
 */
export function resolveCitationLink(
  c: CitationLike,
): { href: string; kind: DeepLinkKind; label: string } | null {
  if (c.href) return { href: c.href, kind: "extern", label: "Beleg öffnen" };
  const hay = `${c.source ?? ""} ${c.path ?? ""} ${c.label ?? ""}`;
  const wp = hay.match(/\/work_packages\/([0-9]+)|work[_-]?package[s]?[^0-9]{0,4}([0-9]{1,6})|\bWP[ #]?([0-9]{1,6})/i);
  if (wp) {
    const id = wp[1] || wp[2] || wp[3];
    const url = openProjectWpUrl(id);
    if (url) return { href: url, kind: "openproject", label: `Arbeitspaket #${id}` };
  }
  const src = (c.source ?? "").toLowerCase();
  if (src.includes("openproject")) return { href: openProjectProgramUrl(), kind: "openproject", label: "In OpenProject" };
  return null;
}
